import { createAsyncThunk } from "@reduxjs/toolkit";
import { ActivationsApi, Configuration, GetActivationsResponse } from "api";
import { downloadFile } from "common/download";
import { getBasePath } from "common/getConfiguration";
import { openSnackbar } from "features/ui";
import { getTranslationID } from "translation";
import { Buffer } from "buffer";
import { createErrorObject } from "errors/createErrorObject";
import { ErrorObject } from "errors/errorObject";
import { PAGE_SIZE } from "./constants";

export const initialAsync = createAsyncThunk<
  GetActivationsResponse,
  void,
  {
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("activation/initialAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();

  const config = new Configuration({ basePath });
  try {
    // アクティベーション一覧を取得するAPI呼び出し
    const activationsApi = new ActivationsApi(config);
    const { data } = await activationsApi.getActivations(PAGE_SIZE, 0);

    return data;
  } catch (e) {
    const error = createErrorObject(e);
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({
      error,
    });
  }
});

export const updateAsync = createAsyncThunk<
  GetActivationsResponse,
  { pageIndex: number },
  {
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("activation/updateAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { pageIndex } = args;

  const config = new Configuration({ basePath });
  try {
    // アクティベーション一覧を取得するAPI呼び出し
    const activationsApi = new ActivationsApi(config);
    const { data } = await activationsApi.getActivations(PAGE_SIZE, pageIndex);

    return data;
  } catch (e) {
    const error = createErrorObject(e);
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: getTranslationID("common.message.serverInternalError"),
      })
    );
    return thunkApi.rejectWithValue({
      error,
    });
  }
});

export const generatePermissionTicketsAsync = createAsyncThunk<
  { success: boolean },
  { aId: string },
  {
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("activation/generatePermissionTicketsAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { aId } = args;

  const config = new Configuration({ basePath });
  try {
    // PermissionTicket生成API呼び出し
    const activationsApi = new ActivationsApi(config);
    const { data } = await activationsApi.generatePermissionTickets(aId);

    downloadFile(
      "permissionTicket",
      Buffer.from(data.permissionTicket, "base64").buffer,
      "application/octet-stream"
    );
    thunkApi.dispatch(
      openSnackbar({
        level: "info",
        message: getTranslationID(
          "activationPage.message.generatePermissionTicketSuccess"
        ),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);
    if (error.errorCode === "E009999") {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID("common.message.serverInternalError"),
        })
      );
    }
    return thunkApi.rejectWithValue({
      error,
    });
  }
});

export const revokeAsync = createAsyncThunk<
  { success: boolean },
  { aId: string; revocationTicket: ArrayBuffer },
  {
    // rejectした時の返却値の型
    rejectValue: {
      error: ErrorObject;
    };
  }
>("activation/revokeAsync", async (args, thunkApi) => {
  // apiのConfigurationを取得する
  const basePath = getBasePath();
  const { aId, revocationTicket } = args;

  const config = new Configuration({ basePath });
  try {
    // アクティベーション一覧を取得するAPI呼び出し
    const activationsApi = new ActivationsApi(config);
    const encodedRevocationTicket =
      Buffer.from(revocationTicket).toString("base64");
    await activationsApi.revoke({
      aId,
      revocationTicket: encodedRevocationTicket,
    });

    thunkApi.dispatch(
      openSnackbar({
        level: "success",
        message: getTranslationID("activationPage.message.revokeSuccess"),
      })
    );
    return { success: true };
  } catch (e) {
    const error = createErrorObject(e);
    // 送信するファイルが巨大すぎる場合は不正なファイルなのでRevocationTicketのエラーとして処理する
    if (error.statusCode === 413) {
      thunkApi.dispatch(
        openSnackbar({
          level: "error",
          message: getTranslationID(
            "activationPage.message.revocationTicketError"
          ),
        })
      );
      return thunkApi.rejectWithValue({
        error,
      });
    }

    const msg =
      error.errorCode === "E010103"
        ? getTranslationID("activationPage.message.revocationTicketError")
        : getTranslationID("common.message.serverInternalError");
    thunkApi.dispatch(
      openSnackbar({
        level: "error",
        message: msg,
      })
    );
    return thunkApi.rejectWithValue({
      error,
    });
  }
});
